import { useCallback, useEffect } from 'react'

import { useTrackBalanceEvent } from '@features/balanceLedger'

import type { PeriodMode } from '../../../enums/periodMode'
import { useSuppliersBalanceFiltersContext } from '../contexts/suppliersBalanceFiltersContext'

interface UseTrackSuppliersBalanceOverviewProps {
  // True until `resultsCount` reflects the current filter/search/period params — covers keepPreviousData still
  // serving stale rows, the initial fetch (nothing to keep yet), and an errored fetch with no cache to fall back on.
  isResultsUnsettled: boolean
  periodType?: PeriodMode
  // Total matching accounts for the current params — the source of both `totalResults` on pagination and the
  // settled `resultsCount` on filter/search once a refetch resolves.
  resultsCount: number
}

interface TrackPaginationProps {
  pageNumber: number
  itemsPerPage: number
}

interface UseTrackSuppliersBalanceOverviewResult {
  trackPagination: (params: TrackPaginationProps) => void
  trackSort: (columnId: string, isDesc: boolean) => void
}

/**
 * Owns the four overview Segment events: `Sorted` and `Pagination Changed` fire immediately, while
 * `Filter Applied` / `Search Used` wait for `pendingInteraction` (set by a real filter/search interaction in
 * `useSuppliersBalanceFilters`) to settle against a non-stale `resultsCount`. This avoids firing a false "applied"
 * event when an advisor lands on a shared link that already carries filters or a search term in the URL.
 */
export const useTrackSuppliersBalanceOverview = ({
  isResultsUnsettled,
  periodType,
  resultsCount,
}: UseTrackSuppliersBalanceOverviewProps): UseTrackSuppliersBalanceOverviewResult => {
  const trackBalanceEvent = useTrackBalanceEvent()
  const { clearPendingInteraction, pendingInteraction } = useSuppliersBalanceFiltersContext()

  useEffect(() => {
    if (!pendingInteraction || isResultsUnsettled) {
      return
    }

    /* eslint-disable @typescript-eslint/naming-convention */
    if (pendingInteraction.kind === 'filter') {
      trackBalanceEvent('Filter Applied', {
        filter_type: pendingInteraction.filterType,
        filter_value: pendingInteraction.filterValue,
        period_type: periodType,
        results_count: resultsCount,
      })
    } else {
      trackBalanceEvent('Search Used', {
        period_type: periodType,
        results_count: resultsCount,
        search_query_length: pendingInteraction.searchQueryLength,
      })
    }
    /* eslint-enable @typescript-eslint/naming-convention */

    clearPendingInteraction()
  }, [clearPendingInteraction, isResultsUnsettled, pendingInteraction, periodType, resultsCount, trackBalanceEvent])

  const trackSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      trackBalanceEvent('Sorted', { column: columnId, direction: isDesc ? 'desc' : 'asc', period_type: periodType })
    },
    [trackBalanceEvent, periodType],
  )

  const trackPagination = useCallback(
    ({ itemsPerPage, pageNumber }: TrackPaginationProps) => {
      /* eslint-disable @typescript-eslint/naming-convention */
      trackBalanceEvent('Pagination Changed', {
        items_per_page: itemsPerPage,
        page_number: pageNumber,
        period_type: periodType,
        total_results: resultsCount,
      })
      /* eslint-enable @typescript-eslint/naming-convention */
    },
    [trackBalanceEvent, periodType, resultsCount],
  )

  return { trackPagination, trackSort }
}
